import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /**
   * Monotonic selection generation. Only the newest variant request may update the visible price.
   * This prevents stale async section responses from overwriting a newer selection.
   * @type {number}
   */
  #selectionGeneration = 0;

  /** @type {{productId: string, saleLabel: string, regularLabel: string, saleFirst: boolean, variants: Map<string, any>} | null | undefined} */
  #instantPriceData;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Reads the compact Shopify-rendered variant price table that is emitted once on product pages.
   */
  #getInstantPriceData() {
    if (this.#instantPriceData !== undefined) return this.#instantPriceData;

    const script = document.getElementById('dc-instant-price-data');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) {
      this.#instantPriceData = null;
      return null;
    }

    try {
      const raw = JSON.parse(script.textContent);
      const variants = new Map(
        Array.isArray(raw.variants)
          ? raw.variants.filter((variant) => variant?.id).map((variant) => [String(variant.id), variant])
          : []
      );

      this.#instantPriceData = {
        productId: String(raw.productId ?? ''),
        saleLabel: String(raw.saleLabel ?? ''),
        regularLabel: String(raw.regularLabel ?? ''),
        saleFirst: raw.saleFirst !== false,
        variants,
      };
    } catch (error) {
      console.warn('[product-price] Instant price data could not be parsed:', error);
      this.#instantPriceData = null;
    }

    return this.#instantPriceData;
  }

  /**
   * Immediately paints the selected variant price from local Shopify-rendered data.
   * The regular Section Rendering response still replaces this with authoritative server HTML afterwards.
   * @param {string | number | null | undefined} variantId
   */
  #renderInstantPrice(variantId) {
    if (variantId == null || variantId === '') return;

    const data = this.#getInstantPriceData();
    if (!data || data.productId !== String(this.dataset.productId ?? '')) return;

    const variant = data.variants.get(String(variantId));
    if (!variant?.eligible || !variant.price) return;

    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!(currentPrice instanceof HTMLElement)) return;

    const nextPrice = document.createElement('div');
    nextPrice.setAttribute('ref', 'priceContainer');

    const createHiddenLabel = (text) => {
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = `${text}\u00a0`;
      return label;
    };

    if (variant.compareAt) {
      const sale = document.createElement('span');
      sale.className = 'price-item__group price';
      sale.append(createHiddenLabel(data.saleLabel), document.createTextNode(String(variant.price)));

      const regular = document.createElement('span');
      regular.className = 'price-item__group price-item--regular compare-at-price';
      regular.append(createHiddenLabel(data.regularLabel), document.createTextNode(String(variant.compareAt)));

      if (data.saleFirst) {
        nextPrice.append(sale, regular);
      } else {
        nextPrice.append(regular, sale);
      }
    } else {
      const price = document.createElement('span');
      price.className = 'price';
      price.textContent = String(variant.price);
      nextPrice.append(price);
    }

    currentPrice.replaceWith(nextPrice);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const generation = ++this.#selectionGeneration;
    this.setAttribute('aria-busy', 'true');

    // Immediate optimistic paint. This uses a local price table generated by Shopify Liquid,
    // so there is no additional request and no waiting for the section-rendering fetch.
    this.#renderInstantPrice(event.detail?.variantId);

    event.promise
      .then(({ detail }) => {
        // A newer selection has already started. Never let an older response write to the UI.
        if (generation !== this.#selectionGeneration) return;
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
          // Combined listings can replace the product and its inline price map.
          this.#instantPriceData = undefined;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Query the CURRENT DOM nodes for every update instead of trusting cached refs.
        // priceContainer is replaced below; a second rapid click can otherwise still point
        // at the detached previous node before Component's MutationObserver refreshes refs.
        const priceContainer = /** @type {HTMLElement | null} */ (
          this.querySelector('[ref="priceContainer"]')
        );
        const volumePricingNote = /** @type {HTMLElement | null} */ (
          this.querySelector('[ref="volumePricingNote"]')
        );

        // Find the new product-price element in the updated HTML.
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render.
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      })
      .finally(() => {
        if (generation === this.#selectionGeneration) {
          this.removeAttribute('aria-busy');
        }
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
